import { LiscioPSPDFKitInstance } from 'types/global';

/**
 * Export the PDF currently loaded in PSPDFKit with all annotations flattened
 * into the page content, then trigger a browser download.
 *
 * Shared by the in-viewer Download toolbar button and the Document-show header
 * Download icon so both produce the same output (raw S3 downloads lose the
 * annotations — LISD-2349 feedback).
 */
export async function downloadFlattenedPdf(
  instance: LiscioPSPDFKitInstance,
  fallbackFileName = 'liscio_file.pdf',
): Promise<void> {
  const buffer = await instance.exportPDF({ flatten: true });
  const fileName = instance.fileName || fallbackFileName;
  const blob = new Blob([buffer], { type: 'application/pdf' });

  // IE 11 support — PSPDFKit renders via the IE-only PdfViewer, so in practice
  // PSPDFKIT_INSTANCE is never set on IE and this branch is defensive. Cast
  // navigator to an intersection type with the optional IE-only method so we
  // type-narrow instead of suppressing errors.
  const ieNavigator = window.navigator as Navigator & {
    msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
  };
  if (ieNavigator.msSaveOrOpenBlob) {
    ieNavigator.msSaveOrOpenBlob(blob, fileName);
    return;
  }

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.style.display = 'none';
  a.download = fileName;
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    // Defer cleanup to the next tick — Firefox has been known to revoke the
    // object URL before the download begins when cleanup is synchronous,
    // particularly on large files. This is the same pattern FileSaver.js uses.
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(a);
    }, 0);
  }
}
